import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Search, Globe, Network, MapPin, Users, Brain, Download,
  Play, RefreshCw, AlertTriangle,
  TrendingUp, Heart, MessageSquare,
  Loader2, FileText, Bot, Languages, Shield,
  Radio, Zap, X as XIcon,
  ChevronDown, ChevronUp, Filter, SortDesc, SortAsc,
  Activity, Info, Link2, Bell as BellIcon,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SocialNetworkGraph from '@/components/SocialNetworkGraph';
import GeoHeatmap from '@/components/GeoHeatmap';
import AccountClusters from '@/components/AccountClusters';
import WordCloud from '@/components/WordCloud';
import { SocialTargetOSINT } from '@/components/social/SocialTargetOSINT';

import {
  PC, PL, ALL_PLATFORMS, fmtNum, parsePosts,
  SocialPost, StreamConfig, AnalysisResult,
} from '@/components/social-stream/types';
import { PostCard } from '@/components/social-stream/PostCard';
import { StreamSidebar } from '@/components/social-stream/StreamSidebar';
import { StreamDetailModal } from '@/components/social-stream/StreamDetailModal';
import { AlertsBell } from '@/components/social-stream/AlertsBell';
import { AlertsTab } from '@/components/social-stream/AlertsTab';
import { AdvancedSearchPanel, AdvancedSearchState, DEFAULT_ADVANCED_STATE } from '@/components/social-stream/AdvancedSearchPanel';
import { EnrichmentPanel } from '@/components/social-stream/EnrichmentPanel';
import { LiveMonitorTab } from '@/components/social-stream/LiveMonitorTab';

// ─── Local helpers / constants kept page-scoped (page-specific chrome, not
// reusable domain components — same pattern SocialStream.tsx used) ──────────

const PHASES = [
  { id: 1, label: 'Collect', icon: Search },
  { id: 2, label: 'Analyze', icon: Brain },
  { id: 3, label: 'Correlate', icon: Network },
  { id: 4, label: 'Report', icon: FileText },
];

const MetricCard = ({
  label, value, icon: Icon, color, sub, onClick,
}: { label: string; value: string | number; icon: React.ElementType; color: string; sub?: string; onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="p-4 rounded-lg border flex flex-col gap-1 relative overflow-hidden text-left w-full transition-all"
    style={{ borderColor: `${color}30`, background: 'rgba(0,0,0,0.7)', cursor: onClick ? 'pointer' : 'default' }}
    onMouseEnter={e => onClick && ((e.currentTarget as HTMLElement).style.borderColor = `${color}70`)}
    onMouseLeave={e => onClick && ((e.currentTarget as HTMLElement).style.borderColor = `${color}30`)}
  >
    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at top left, ${color}06 0%, transparent 70%)` }} />
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: `${color}80` }}>{label}</span>
      <div className="flex items-center gap-1.5">
        <Icon size={13} style={{ color: `${color}60` }} />
        {onClick && <Info size={10} style={{ color: `${color}40` }} />}
      </div>
    </div>
    <span className="text-2xl font-bold text-white tabular-nums">{value}</span>
    {sub && <span className="text-[10px] text-gray-600">{sub}</span>}
  </button>
);

const EmptyTabState = ({ icon: Icon, label }: { icon: React.ElementType; label: string }) => (
  <div className="flex flex-col items-center justify-center h-full">
    <Icon size={32} className="text-gray-700 mb-3" />
    <p className="text-sm text-gray-600">{label}</p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const SocialIntelligence: React.FC = () => {
  const queryClient = useQueryClient();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['twitter', 'instagram', 'facebook', 'youtube']);
  const [activeKeyword, setActiveKeyword] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState<AdvancedSearchState>(DEFAULT_ADVANCED_STATE);
  const [searchHistory, setSearchHistory] = useState<Array<{ query: string; platforms: string[] }>>([]);
  const [alsoMonitor, setAlsoMonitor] = useState(true); // explicit, default-checked — replaces the old hidden side effect
  const [monitorInterval, setMonitorInterval] = useState(60);
  const [monitorEmailAlerts, setMonitorEmailAlerts] = useState(true);

  // Search history is now genuinely persisted server-side (social_search_history,
  // previously a pair of no-op stub endpoints) — seed from it on mount instead of
  // starting empty every session.
  useEffect(() => {
    apiClient.getSearchHistory()
      .then((res: any) => {
        const hist = (res?.history ?? []) as Array<{ query: string; platforms: string[] }>;
        if (hist.length) setSearchHistory(hist.map(h => ({ query: h.query, platforms: h.platforms ?? [] })));
      })
      .catch(() => {});
  }, []);

  // Data state
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [platformCursors, setPlatformCursors] = useState<Record<string, string>>({});
  const [hasMorePlatforms, setHasMorePlatforms] = useState<string[]>([]);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [phase, setPhase] = useState(1);
  const [intelligenceResult, setIntelligenceResult] = useState<any>(null);
  const [iocResult, setIocResult] = useState<any>(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [enrichment, setEnrichment] = useState<any>(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);

  // UI state
  const [viewMode, setViewMode] = useState<'stream' | 'live' | 'target'>('stream');
  const [activeTab, setActiveTab] = useState('feed');
  const [period, setPeriod] = useState('all');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'timestamp' | 'likes' | 'shares'>('relevance');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [tlpLevel, setTlpLevel] = useState<'WHITE' | 'GREEN' | 'AMBER' | 'RED'>('AMBER');
  const [stixLoading, setStixLoading] = useState(false);

  // Modal state
  const [metricModal, setMetricModal] = useState<null | { label: string; color: string; rows: Array<{ label: string; value: string | number }> }>(null);
  const [postModal, setPostModal] = useState<SocialPost | null>(null);
  const [streamModal, setStreamModal] = useState<StreamConfig | null>(null);

  const { data: streamConfigs = [] } = useQuery<StreamConfig[]>({
    queryKey: ['social-streams'],
    queryFn: async () => {
      const res = await apiClient.listStreams(true) as any;
      return (Array.isArray(res) ? res : (res?.streams ?? [])) as StreamConfig[];
    },
    refetchInterval: 30_000,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const searchMutation = useMutation({
    mutationFn: (params: { query: string; platforms: string[] }) =>
      apiClient.socialSearch({ query: params.query, platforms: params.platforms, count_per_platform: 200, sort_by: sortBy === 'relevance' ? 'relevance' : 'recency' }),
    onSuccess: (data: any) => {
      const parsed = parsePosts(data);
      setPosts(parsed);
      setActiveKeyword(searchQuery);
      setAnalysisResult(null);
      setIntelligenceResult(null);
      setIocResult(null);
      setPhase(parsed.length > 0 ? 2 : 1);

      const platformResults = data?.search_metadata?.platform_results ?? {};
      const cursors: Record<string, string> = {};
      const more: string[] = [];
      Object.entries(platformResults).forEach(([plat, r]: [string, any]) => {
        if (r?.next_cursor) { cursors[plat] = r.next_cursor; if (r?.has_more) more.push(plat); }
      });
      setPlatformCursors(cursors);
      setHasMorePlatforms(more);

      setSearchHistory(prev => [{ query: searchQuery, platforms: selectedPlatforms }, ...prev.filter(h => h.query !== searchQuery)].slice(0, 10));

      // Best-effort server-side persistence (social_search_history) — the
      // search_history state above is just local-session cache, seeded from
      // and synced to this on mount/each search so it survives a reload.
      apiClient.saveSearchQuery({
        query: searchQuery,
        platforms: selectedPlatforms,
        entity_type: advanced.entityType,
        count_per_platform: advanced.countPerPlatform,
        results_count: parsed.length,
        timestamp: new Date().toISOString(),
      }).catch(() => {});

      // Explicit, visible stream creation — replaces the old silent
      // fire-and-forget-on-every-search side effect.
      if (alsoMonitor) {
        apiClient.createStream({ keyword: searchQuery, platforms: selectedPlatforms, fetch_interval_minutes: monitorInterval, email_alerts_enabled: monitorEmailAlerts })
          .then(() => queryClient.invalidateQueries({ queryKey: ['social-streams'] }))
          .catch(() => {});
      }
    },
  });

  const loadMoreMutation = useMutation({
    mutationFn: () => apiClient.socialSearch({ query: activeKeyword, platforms: Object.keys(platformCursors), count_per_platform: 200, cursors: platformCursors, sort_by: sortBy === 'relevance' ? 'relevance' : 'recency' }),
    onSuccess: (data: any) => {
      const parsed = parsePosts(data);
      setPosts(prev => {
        const seen = new Set(prev.map(p => p.id));
        return [...prev, ...parsed.filter(p => !seen.has(p.id))];
      });
      const platformResults = data?.search_metadata?.platform_results ?? {};
      const cursors: Record<string, string> = {};
      const more: string[] = [];
      Object.entries(platformResults).forEach(([plat, r]: [string, any]) => {
        if (r?.next_cursor) { cursors[plat] = r.next_cursor; if (r?.has_more) more.push(plat); }
      });
      setPlatformCursors(cursors);
      setHasMorePlatforms(more);
    },
  });

  const toggleStreamMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? apiClient.activateStream(id) : apiClient.deactivateStream(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-streams'] }),
  });

  const deleteStreamMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteStream(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['social-streams'] }); setStreamModal(null); },
  });

  const addStreamMutation = useMutation({
    mutationFn: (data: { keyword: string; platforms: string[]; fetch_interval_minutes: number; email_alerts_enabled: boolean }) =>
      apiClient.createStream(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-streams'] }),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const runSearch = useCallback((query: string, platforms: string[]) => {
    if (!query.trim() || platforms.length === 0) return;
    setPosts([]);
    setPhase(1);
    setEnrichment(null);
    searchMutation.mutate({ query: query.trim(), platforms });

    const advancedActive = showAdvanced && (advanced.includeNews || advanced.includeMarketData || advanced.sentimentAnalysis || advanced.trendAnalysis);
    if (advancedActive) {
      setEnrichmentLoading(true);
      apiClient.socialListeningSearch({
        query: query.trim(), platforms, entity_type: advanced.entityType, count_per_platform: advanced.countPerPlatform,
        include_news: advanced.includeNews, include_market_data: advanced.includeMarketData,
        sentiment_analysis: advanced.sentimentAnalysis, trend_analysis: advanced.trendAnalysis,
      } as any)
        .then((res: any) => setEnrichment(res))
        .catch(() => setEnrichment(null))
        .finally(() => setEnrichmentLoading(false));
    }
  }, [showAdvanced, advanced, searchMutation]);

  const handleSearch = useCallback(() => runSearch(searchQuery, selectedPlatforms), [runSearch, searchQuery, selectedPlatforms]);

  const togglePlatform = (p: string) =>
    setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const runAnalysis = async () => {
    if (!posts.length || analyzing) return;
    setAnalyzing(true);
    try {
      const result = await apiClient.analyzePosts(posts);
      setAnalysisResult(result as AnalysisResult);
      setPhase(3);
    } catch (e) { console.error('Analysis failed', e); }
    finally { setAnalyzing(false); }
  };

  const runIntelligence = async () => {
    if (!posts.length || intelligenceLoading) return;
    setIntelligenceLoading(true);
    try {
      const result = await apiClient.socialIntelligence(posts, activeKeyword);
      setIntelligenceResult(result);
      setIocResult((result as any).iocs);
    } catch (e) { console.error('Intelligence failed', e); }
    finally { setIntelligenceLoading(false); }
  };

  const handleExportPDF = async () => {
    if (!posts.length || pdfLoading) return;
    setPdfLoading(true);
    try {
      const sentimentStats = {
        positive: posts.filter(p => p.sentiment === 'positive').length,
        negative: posts.filter(p => p.sentiment === 'negative').length,
        neutral: posts.filter(p => p.sentiment === 'neutral').length,
        unknown: posts.filter(p => p.sentiment === 'unknown').length,
        total: posts.length,
      };
      await apiClient.generateSocialPDF({
        posts, query: activeKeyword, sentimentStats, includeCharts: true, tlp_level: tlpLevel,
        analysis: analysisResult ?? undefined, iocs: iocResult ?? undefined,
      });
      setPhase(4);
    } catch (e) { console.error('PDF failed', e); }
    finally { setPdfLoading(false); }
  };

  const handleExportStix = async () => {
    if (!posts.length || stixLoading) return;
    setStixLoading(true);
    try {
      await apiClient.socialStixExport(activeKeyword, posts, iocResult ?? {}, analysisResult?.narrative_clusters ?? []);
    } catch (e) { console.error('STIX export failed', e); }
    finally { setStixLoading(false); }
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify({ keyword: activeKeyword, posts, analysis: analysisResult }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `sycek-social-${activeKeyword}-${Date.now()}.json`; a.click();
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const PERIOD_MS: Record<string, number> = { '1d': 86400000, '7d': 604800000, '30d': 2592000000, '3m': 7776000000 };

  const periodFilteredPosts = useMemo(() => {
    if (period === 'all' || !PERIOD_MS[period]) return posts;
    const cutoff = Date.now() - PERIOD_MS[period];
    return posts.filter(p => new Date(p.timestamp).getTime() >= cutoff);
  }, [posts, period]);

  const filteredPosts = useMemo(() => {
    let arr = [...periodFilteredPosts];
    if (sentimentFilter !== 'all') arr = arr.filter(p => p.sentiment === sentimentFilter);
    if (platformFilter !== 'all') arr = arr.filter(p => p.platform === platformFilter);
    arr.sort((a, b) => {
      const va = sortBy === 'relevance' ? (a.relevance_score ?? 0) : sortBy === 'timestamp' ? new Date(a.timestamp).getTime()
        : sortBy === 'likes' ? a.likes : a.shares;
      const vb = sortBy === 'relevance' ? (b.relevance_score ?? 0) : sortBy === 'timestamp' ? new Date(b.timestamp).getTime()
        : sortBy === 'likes' ? b.likes : b.shares;
      return sortOrder === 'desc' ? vb - va : va - vb;
    });
    return arr;
  }, [periodFilteredPosts, sentimentFilter, platformFilter, sortBy, sortOrder]);

  const metrics = useMemo(() => {
    const src = periodFilteredPosts;
    if (!src.length) return null;
    const authors = new Set(src.map(p => p.author_id)).size;
    const platforms = [...new Set(src.map(p => p.platform))];
    const negative = src.filter(p => p.sentiment === 'negative').length;
    const langs = src.reduce((a, p) => { a[p.language || 'en'] = (a[p.language || 'en'] || 0) + 1; return a; }, {} as Record<string, number>);
    const topLang = Object.entries(langs).sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'en';
    const botRisk = src.filter(p => {
      const engRate = p.author_followers > 0 ? (p.likes + p.shares) / p.author_followers : 0;
      return engRate < 0.001 && p.author_followers > 1000;
    }).length;
    return { authors, platforms, negative, topLang, botRisk };
  }, [periodFilteredPosts]);

  const platformBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    periodFilteredPosts.forEach(p => { map[p.platform] = (map[p.platform] || 0) + 1; });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [periodFilteredPosts]);

  const sentimentChartData = useMemo(() => {
    const c = { positive: 0, negative: 0, neutral: 0 };
    periodFilteredPosts.forEach(p => { if (p.sentiment in c) c[p.sentiment as keyof typeof c]++; });
    return [
      { name: 'Positive', value: c.positive, fill: '#22c55e' },
      { name: 'Negative', value: c.negative, fill: '#ef4444' },
      { name: 'Neutral', value: c.neutral, fill: '#6b7280' },
    ].filter(d => d.value > 0);
  }, [periodFilteredPosts]);

  const wordCloudData = useMemo(() => {
    const stop = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'is', 'was', 'are', 'were', 'be', 'have', 'has', 'do', 'this', 'that', 'it', 'not', 'no', 'so', 'as', 'up', 'out', 'rt', 'amp', 'https', 'http', 'will', 'can', 'just', 'from', 'they', 'he', 'she', 'we', 'you', 'his', 'her', 'their', 'our', 'my', 'by']);
    const counts: Record<string, number> = {};
    periodFilteredPosts.forEach(p => {
      p.text.toLowerCase().split(/\s+/).forEach(w => {
        const c = w.replace(/[^a-z0-9]/g, '');
        if (c.length > 3 && !stop.has(c)) counts[c] = (counts[c] || 0) + 1;
      });
    });
    return Object.entries(counts).sort(([, a], [, b]) => b - a).slice(0, 60).map(([text, value]) => ({ text, value }));
  }, [periodFilteredPosts]);

  const timelineData = useMemo(() => {
    const hours: Record<number, number> = {};
    periodFilteredPosts.forEach(p => { const h = new Date(p.timestamp).getHours(); hours[h] = (hours[h] || 0) + 1; });
    return Array.from({ length: 24 }, (_, h) => ({ hour: `${h}:00`, posts: hours[h] || 0 }));
  }, [periodFilteredPosts]);

  const hasData = posts.length > 0;
  const isLoading = searchMutation.isPending;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#080808', color: '#e5e7eb' }}>

      {viewMode === 'stream' && (
        <StreamSidebar
          streams={streamConfigs as StreamConfig[]}
          onToggle={(id, active) => toggleStreamMutation.mutate({ id, active })}
          onDelete={id => deleteStreamMutation.mutate(id)}
          onSelect={setStreamModal}
          onAdd={data => addStreamMutation.mutate(data)}
          adding={addStreamMutation.isPending}
          platformBreakdown={platformBreakdown}
          totalPosts={posts.length}
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Page header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3" style={{ borderBottom: '1px solid #1a1a1a', background: '#0a0a0a' }}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Activity size={16} style={{ color: '#00ff00' }} />
              <span className="text-sm font-mono font-bold tracking-widest uppercase" style={{ color: '#00ff00' }}>
                Social Intelligence
              </span>
            </div>
            {hasData && viewMode === 'stream' && (
              <span className="text-[11px] text-gray-500 font-mono">
                — {posts.length} signals across {[...new Set(posts.map(p => p.platform))].length} platforms for <span style={{ color: '#00ff00' }}>"{activeKeyword}"</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <AlertsBell onViewAll={() => { setViewMode('stream'); setActiveTab('alerts'); }} />
            <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid #1a1a1a', background: '#0a0a0a' }}>
              {([['stream', 'Stream', Radio, '#00ff00'], ['live', 'Live Monitor', Play, '#eab308'], ['target', 'Target OSINT', Search, '#3b82f6']] as const).map(([mode, label, Icon, color], i) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-all"
                  style={{ background: viewMode === mode ? `${color}15` : 'transparent', color: viewMode === mode ? color : '#4b5563', borderRight: i < 2 ? '1px solid #1a1a1a' : undefined }}>
                  <Icon size={11} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {viewMode === 'target' && (
          <div className="flex-1 overflow-y-auto p-6"><SocialTargetOSINT /></div>
        )}

        {viewMode === 'live' && (
          <div className="flex-1 overflow-y-auto"><LiveMonitorTab /></div>
        )}

        {viewMode === 'stream' && <>

          {/* Search command bar */}
          <div className="flex-shrink-0" style={{ borderBottom: '1px solid #1a1a1a', background: '#0c0c0c' }}>
            <div className="p-4">
              <div className="flex gap-3 items-start">
                <div className="flex-1">
                  <Input
                    placeholder="Enter keyword, hashtag, or topic to investigate..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    className="h-10 text-sm font-mono"
                    style={{ background: '#111', border: '1px solid #333', color: '#e5e7eb', caretColor: '#00ff00' }}
                  />
                </div>
                <Button onClick={handleSearch} disabled={!searchQuery.trim() || selectedPlatforms.length === 0 || isLoading}
                  className="h-10 px-6 font-mono text-sm font-bold"
                  style={{ background: isLoading ? '#111' : '#00ff0015', border: '1px solid #00ff0050', color: '#00ff00' }}>
                  {isLoading ? <><Loader2 size={14} className="mr-2 animate-spin" />Collecting...</> : <><Search size={14} className="mr-2" />Investigate</>}
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-[10px] text-gray-600 font-mono uppercase self-center mr-1">Platforms:</span>
                {ALL_PLATFORMS.map(p => (
                  <button key={p} onClick={() => togglePlatform(p)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono transition-all"
                    style={{
                      background: selectedPlatforms.includes(p) ? `${PC[p]}20` : '#111',
                      border: `1px solid ${selectedPlatforms.includes(p) ? PC[p] + '60' : '#222'}`,
                      color: selectedPlatforms.includes(p) ? PC[p] : '#4b5563',
                    }}>
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: selectedPlatforms.includes(p) ? PC[p] : '#374151' }} />
                    {PL[p]}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-[10px] text-gray-600 font-mono uppercase">Timeframe:</span>
                  {[{ v: 'all', l: 'All time' }, { v: '1d', l: '24h' }, { v: '7d', l: '7 days' }, { v: '30d', l: '30 days' }, { v: '3m', l: '3 months' }].map(({ v, l }) => (
                    <button key={v} onClick={() => setPeriod(v)}
                      className="px-2.5 py-1 rounded text-[10px] font-mono transition-all"
                      style={{ background: period === v ? '#00ff0015' : '#111', border: `1px solid ${period === v ? '#00ff0040' : '#222'}`, color: period === v ? '#00ff00' : '#4b5563' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Explicit monitor-creation + advanced toggle row */}
              <div className="flex items-center gap-4 mt-3">
                <label className="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={alsoMonitor} onChange={e => setAlsoMonitor(e.target.checked)} className="w-3 h-3 accent-[#00ff00]" />
                  Also create a monitoring stream
                </label>
                {alsoMonitor && (
                  <>
                    <Select value={String(monitorInterval)} onValueChange={v => setMonitorInterval(Number(v))}>
                      <SelectTrigger className="h-6 text-[10px] w-28" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
                        {[15, 30, 60, 180, 360].map(m => (
                          <SelectItem key={m} value={String(m)} style={{ color: '#e5e7eb' }}>{m < 60 ? `${m}min` : `${m / 60}h`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                      <input type="checkbox" checked={monitorEmailAlerts} onChange={e => setMonitorEmailAlerts(e.target.checked)} className="w-3 h-3 accent-[#00ff00]" />
                      Email alerts
                    </label>
                  </>
                )}
                <button onClick={() => setShowAdvanced(v => !v)} className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300">
                  Advanced {showAdvanced ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
              </div>
            </div>

            {showAdvanced && (
              <AdvancedSearchPanel
                value={advanced}
                onChange={setAdvanced}
                searchHistory={searchHistory}
                onPickHistory={(q, p) => { setSearchQuery(q); setSelectedPlatforms(p); }}
              />
            )}
          </div>

          {/* Phase indicator */}
          {hasData && (
            <div className="flex-shrink-0 px-6 py-3 flex items-center gap-0" style={{ borderBottom: '1px solid #1a1a1a', background: '#0a0a0a' }}>
              {PHASES.map((ph, i) => {
                const active = phase === ph.id;
                const done = phase > ph.id;
                const Icon = ph.icon;
                return (
                  <React.Fragment key={ph.id}>
                    <button
                      onClick={() => { if (ph.id === 2) { setActiveTab('narratives'); runAnalysis(); } if (ph.id === 3) setActiveTab('narratives'); if (ph.id === 4) setActiveTab('export'); }}
                      disabled={!hasData || ph.id === 1}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-xs transition-all disabled:cursor-default"
                      style={{ background: done ? '#00ff0010' : active ? '#00ff0020' : '#111', border: `1px solid ${done || active ? '#00ff0040' : '#222'}`, color: done || active ? '#00ff00' : '#4b5563' }}>
                      <Icon size={11} /><span>{ph.label}</span>{done && <span style={{ color: '#00ff00' }}>✓</span>}
                    </button>
                    {i < PHASES.length - 1 && <div className="w-8 h-px mx-1" style={{ background: phase > ph.id ? '#00ff0040' : '#1a1a1a' }} />}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {enrichmentLoading || enrichment ? (
            <EnrichmentPanel loading={enrichmentLoading} newsArticles={enrichment?.news_articles} marketData={enrichment?.market_data} aiSynthesis={enrichment?.ai_synthesis} />
          ) : null}

          {/* Metric cards */}
          {hasData && metrics && (
            <div className="flex-shrink-0 grid grid-cols-6 gap-3 px-6 py-4" style={{ borderBottom: '1px solid #1a1a1a' }}>
              <MetricCard label="Total Signals" value={posts.length} icon={MessageSquare} color="#00ff00" sub="posts collected"
                onClick={() => setMetricModal({ label: 'Total Signals', color: '#00ff00', rows: [
                  { label: 'Total posts', value: posts.length },
                  { label: 'With media', value: posts.filter(p => p.media_urls.length > 0).length },
                  { label: 'With hashtags', value: posts.filter(p => p.hashtags.length > 0).length },
                ]})} />
              <MetricCard label="Unique Authors" value={metrics.authors} icon={Users} color="#00aaff" sub="distinct accounts"
                onClick={() => setMetricModal({ label: 'Unique Authors', color: '#00aaff', rows: [
                  { label: 'Unique accounts', value: metrics.authors },
                  { label: 'Verified', value: posts.filter(p => p.author_verified).length },
                  { label: 'With >1K followers', value: posts.filter(p => p.author_followers > 1000).length },
                ]})} />
              <MetricCard label="Platforms" value={metrics.platforms.length} icon={Globe} color="#a855f7" sub={metrics.platforms.map(p => PL[p] ?? p).join(', ')}
                onClick={() => setMetricModal({ label: 'Platform Breakdown', color: '#a855f7', rows: platformBreakdown.map(([p, n]) => ({ label: PL[p] ?? p, value: `${n} posts` })) })} />
              <MetricCard label="Negative Signals" value={metrics.negative} icon={AlertTriangle} color="#ef4444" sub={`${posts.length > 0 ? Math.round(metrics.negative / posts.length * 100) : 0}% of corpus`}
                onClick={() => setMetricModal({ label: 'Sentiment Breakdown', color: '#ef4444', rows: [
                  { label: 'Positive', value: posts.filter(p => p.sentiment === 'positive').length },
                  { label: 'Negative', value: posts.filter(p => p.sentiment === 'negative').length },
                  { label: 'Neutral', value: posts.filter(p => p.sentiment === 'neutral').length },
                ]})} />
              <MetricCard label={intelligenceResult?.cib ? 'CIB Score' : 'Bot Risk'} value={intelligenceResult?.cib?.cib_score ?? metrics.botRisk} icon={Bot}
                color={intelligenceResult?.cib?.coordination_flag ? '#ef4444' : '#f97316'} sub={intelligenceResult?.cib?.coordination_flag ? 'Coordination detected' : 'suspicious accounts'}
                onClick={() => setMetricModal({ label: 'Bot / CIB Analysis', color: '#f97316', rows: [
                  { label: 'Suspicious accounts (local)', value: metrics.botRisk },
                  { label: 'CIB Score (0-100)', value: intelligenceResult?.cib?.cib_score ?? 'run intelligence' },
                ]})} />
              <MetricCard label="Top Language" value={metrics.topLang.toUpperCase()} icon={Languages} color="#22c55e"
                sub={`${Math.round(posts.filter(p => (p.language || 'en') === metrics.topLang).length / posts.length * 100)}% of posts`}
                onClick={() => {
                  const langs = posts.reduce((a, p) => { const l = p.language || 'en'; a[l] = (a[l] || 0) + 1; return a; }, {} as Record<string, number>);
                  setMetricModal({ label: 'Language Distribution', color: '#22c55e', rows: Object.entries(langs).sort(([, a], [, b]) => b - a).map(([l, n]) => ({ label: l.toUpperCase(), value: `${n} posts` })) });
                }} />
            </div>
          )}

          {/* Tabs */}
          <div className="flex-1 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <TabsList className="flex-shrink-0 w-full justify-start rounded-none px-6 gap-0 h-10" style={{ background: '#0a0a0a', borderBottom: '1px solid #1a1a1a' }}>
                {[
                  { value: 'feed', label: 'Intelligence Feed', icon: MessageSquare },
                  { value: 'network', label: 'Network', icon: Network },
                  { value: 'geo', label: 'Geographic', icon: MapPin },
                  { value: 'accounts', label: 'Accounts', icon: Bot },
                  { value: 'narratives', label: 'Narratives', icon: Brain },
                  { value: 'iocs', label: 'IOC Surface', icon: Shield },
                  { value: 'alerts', label: 'Alerts', icon: BellIcon },
                  { value: 'export', label: 'Export', icon: Download },
                ].map(t => (
                  <TabsTrigger key={t.value} value={t.value}
                    className="flex items-center gap-1.5 px-4 h-full rounded-none text-xs font-mono data-[state=active]:bg-transparent"
                    style={{ borderBottom: activeTab === t.value ? '2px solid #00ff00' : '2px solid transparent', color: activeTab === t.value ? '#00ff00' : '#4b5563' }}>
                    <t.icon size={12} />{t.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Feed */}
              <TabsContent value="feed" className="flex-1 overflow-hidden m-0 data-[state=active]:flex flex-col">
                {hasData && (
                  <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3" style={{ borderBottom: '1px solid #1a1a1a', background: '#0c0c0c' }}>
                    <Filter size={12} className="text-gray-600" />
                    <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
                      <SelectTrigger className="h-7 text-[11px] w-32" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}><SelectValue /></SelectTrigger>
                      <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
                        {['all', 'positive', 'negative', 'neutral'].map(v => <SelectItem key={v} value={v} style={{ color: '#e5e7eb' }}>{v === 'all' ? 'All sentiments' : v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={platformFilter} onValueChange={setPlatformFilter}>
                      <SelectTrigger className="h-7 text-[11px] w-32" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}><SelectValue /></SelectTrigger>
                      <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
                        <SelectItem value="all" style={{ color: '#e5e7eb' }}>All platforms</SelectItem>
                        {[...new Set(posts.map(p => p.platform))].map(p => <SelectItem key={p} value={p} style={{ color: '#e5e7eb' }}>{PL[p] ?? p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={sortBy} onValueChange={v => setSortBy(v as any)}>
                      <SelectTrigger className="h-7 text-[11px] w-32" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}><SelectValue /></SelectTrigger>
                      <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
                        {[['relevance', 'Relevance'], ['timestamp', 'Date'], ['likes', 'Likes'], ['shares', 'Shares']].map(([v, l]) => <SelectItem key={v} value={v} style={{ color: '#e5e7eb' }}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono text-gray-500 hover:text-gray-300" style={{ background: '#111', border: '1px solid #222' }}>
                      {sortOrder === 'desc' ? <SortDesc size={11} /> : <SortAsc size={11} />}{sortOrder === 'desc' ? 'Desc' : 'Asc'}
                    </button>
                    <span className="ml-auto text-[10px] text-gray-600 font-mono">{filteredPosts.length} / {periodFilteredPosts.length} posts</span>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto">
                  {!hasData && !isLoading && (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Search size={36} style={{ color: '#00ff0040' }} className="mb-6" />
                      <p className="text-base font-mono font-bold text-gray-400 mb-2">No Intelligence Collected</p>
                      <p className="text-sm text-gray-600 max-w-sm text-center">Enter a keyword above and select platforms to begin cross-platform intelligence gathering.</p>
                    </div>
                  )}
                  {isLoading && (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Loader2 size={36} className="animate-spin mb-4" style={{ color: '#00ff00' }} />
                      <p className="text-sm font-mono text-gray-400">Collecting signals from {selectedPlatforms.length} platforms...</p>
                    </div>
                  )}
                  {hasData && (
                    <div className="flex gap-6 p-6">
                      <div className="flex-1 min-w-0">
                        {filteredPosts.map(post => <PostCard key={post.id} post={post} keyword={activeKeyword} onAnalyze={setPostModal} />)}
                        {filteredPosts.length === 0 && <div className="text-center py-16"><p className="text-sm text-gray-600">No posts match the current filters.</p></div>}
                        {hasMorePlatforms.length > 0 && (
                          <Button className="w-full h-9 text-xs font-mono mt-2" disabled={loadMoreMutation.isPending}
                            style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}
                            onClick={() => loadMoreMutation.mutate()}>
                            {loadMoreMutation.isPending ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : null}
                            Load more ({hasMorePlatforms.map(p => PL[p] ?? p).join(', ')})
                          </Button>
                        )}
                      </div>
                      <div className="w-64 flex-shrink-0 space-y-4">
                        <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-3">Sentiment Split</p>
                          <ResponsiveContainer width="100%" height={120}>
                            <PieChart>
                              <Pie data={sentimentChartData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={3}>
                                {sentimentChartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                              </Pie>
                              <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 11 }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-3">Posting Activity (UTC)</p>
                          <ResponsiveContainer width="100%" height={80}>
                            <AreaChart data={timelineData}>
                              <defs><linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00ff00" stopOpacity={0.3} /><stop offset="95%" stopColor="#00ff00" stopOpacity={0} /></linearGradient></defs>
                              <Area type="monotone" dataKey="posts" stroke="#00ff00" strokeWidth={1.5} fill="url(#actGrad)" />
                              <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 10 }} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                        {wordCloudData.length > 0 && (
                          <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-3">Top Terms</p>
                            <WordCloud words={wordCloudData} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="network" className="flex-1 m-0" style={{ overflow: 'hidden', minHeight: 0 }}>
                {hasData ? <div style={{ height: '100%', padding: '1rem' }}><SocialNetworkGraph posts={posts} /></div> : <EmptyTabState icon={Network} label="Run a search to see the network graph." />}
              </TabsContent>

              <TabsContent value="geo" className="flex-1 m-0" style={{ overflowY: 'auto', minHeight: 0 }}>
                {hasData ? <div style={{ padding: '1rem' }}><GeoHeatmap posts={posts} /></div> : <EmptyTabState icon={MapPin} label="Run a search to see geographic distribution." />}
              </TabsContent>

              <TabsContent value="accounts" className="flex-1 m-0" style={{ overflowY: 'auto', minHeight: 0 }}>
                {hasData ? <div style={{ padding: '1rem' }}><AccountClusters posts={periodFilteredPosts} /></div> : <EmptyTabState icon={Users} label="Run a search to analyze account clusters." />}
              </TabsContent>

              <TabsContent value="narratives" className="flex-1 m-0" style={{ overflowY: 'auto', minHeight: 0 }}>
                {!hasData ? <EmptyTabState icon={Brain} label="Run a search first to enable narrative analysis." /> : (
                  <div className="p-6 space-y-6">
                    {!analysisResult && (
                      <div className="flex flex-col items-center py-12">
                        <Brain size={32} style={{ color: '#00ff0050' }} className="mb-6" />
                        <p className="text-sm text-gray-400 font-mono mb-2">Narrative Analysis Ready</p>
                        <Button onClick={runAnalysis} disabled={analyzing} className="px-8 h-10 font-mono text-sm" style={{ background: '#00ff0015', border: '1px solid #00ff0050', color: '#00ff00' }}>
                          {analyzing ? <><Loader2 size={14} className="mr-2 animate-spin" />Analyzing...</> : <><Brain size={14} className="mr-2" />Run Narrative Analysis</>}
                        </Button>
                      </div>
                    )}
                    {analysisResult && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-gray-400">Analysis complete</span>
                          <div className="flex items-center gap-3">
                            <button onClick={runIntelligence} disabled={intelligenceLoading} className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded" style={{ color: '#ef444480', border: '1px solid #ef444420' }}>
                              <Shield size={10} />{intelligenceResult ? 'Re-run Intel' : 'Run Intelligence'}
                            </button>
                            <button onClick={runAnalysis} disabled={analyzing} className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300">
                              <RefreshCw size={11} className={analyzing ? 'animate-spin' : ''} /> Re-run
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                          <div className="rounded-lg p-5" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                            <div className="flex items-center gap-2 mb-4"><TrendingUp size={14} style={{ color: '#00aaff' }} /><p className="text-xs font-mono uppercase tracking-widest" style={{ color: '#00aaff' }}>Top Influencers</p></div>
                            <div className="space-y-3">
                              {(analysisResult.top_influencers ?? []).slice(0, 10).map((inf, i) => (
                                <div key={i} className="flex items-center gap-3">
                                  <span className="text-[10px] font-mono text-gray-600 w-5 text-right">{i + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className="text-xs text-white truncate">@{inf.author}</span>
                                      <span className="text-[10px] font-mono" style={{ color: '#00aaff' }}>{(inf.impact_score ?? inf.influence_score)?.toFixed(1) ?? '—'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] text-gray-600">
                                      <span className="flex items-center gap-0.5"><Users size={9} /> {fmtNum(inf.followers ?? 0)}</span>
                                      <span className="flex items-center gap-0.5"><Heart size={9} /> {fmtNum(inf.likes ?? 0)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-lg p-5" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                            <div className="flex items-center gap-2 mb-4"><Zap size={14} style={{ color: '#a855f7' }} /><p className="text-xs font-mono uppercase tracking-widest" style={{ color: '#a855f7' }}>Narrative Clusters</p></div>
                            {(analysisResult.narrative_clusters?.length ?? 0) > 0 ? (
                              <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={(analysisResult.narrative_clusters ?? []).slice(0, 8).map(c => {
                                  const kws = c.terms ?? c.keywords ?? ['?'];
                                  const sd = c.sentiment_breakdown ?? c.sentiment_distribution ?? {};
                                  return { name: kws[0], Positive: sd.positive ?? 0, Neutral: sd.neutral ?? 0, Negative: sd.negative ?? 0 };
                                })} layout="vertical" margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
                                  <XAxis type="number" tick={{ fontSize: 9, fill: '#4b5563' }} />
                                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} width={72} />
                                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 11 }} />
                                  <Bar dataKey="Positive" stackId="a" fill="#22c55e" /><Bar dataKey="Neutral" stackId="a" fill="#374151" /><Bar dataKey="Negative" stackId="a" fill="#ef4444" />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : <p className="text-xs text-gray-600 text-center py-8">No clusters detected</p>}
                          </div>
                        </div>
                        {(intelligenceResult?.cib?.coordination_flag || (metrics && metrics.botRisk > 3)) && (
                          <div className="rounded-lg p-4 flex items-start gap-3" style={{ background: intelligenceResult?.cib?.coordination_flag ? '#1a000a' : '#1a0a00', border: `1px solid ${intelligenceResult?.cib?.coordination_flag ? '#ef444440' : '#f9731640'}` }}>
                            <AlertTriangle size={16} style={{ color: intelligenceResult?.cib?.coordination_flag ? '#ef4444' : '#f97316', flexShrink: 0, marginTop: 2 }} />
                            <div>
                              <p className="text-xs font-mono font-bold" style={{ color: intelligenceResult?.cib?.coordination_flag ? '#ef4444' : '#f97316' }}>
                                {intelligenceResult?.cib?.coordination_flag ? 'Coordinated Inauthentic Behavior Detected' : 'Potential Coordinated Activity Detected'}
                              </p>
                              <p className="text-[11px] text-gray-500 mt-1">
                                {intelligenceResult?.cib ? intelligenceResult.cib.summary : `${metrics?.botRisk} accounts show low engagement relative to follower counts.`}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="iocs" className="flex-1 m-0" style={{ overflowY: 'auto', minHeight: 0 }}>
                <div className="p-6 space-y-6">
                  {!hasData ? (
                    <div className="flex flex-col items-center py-16"><Shield size={32} style={{ color: '#374151', marginBottom: 16 }} /><p className="text-sm text-gray-600 font-mono">Run a search first to surface IOCs.</p></div>
                  ) : !iocResult ? (
                    <div className="flex flex-col items-center py-12">
                      <Shield size={32} style={{ color: '#ef444450' }} className="mb-6" />
                      <p className="text-sm text-gray-400 font-mono mb-2">IOC Surface Ready</p>
                      <Button onClick={runIntelligence} disabled={intelligenceLoading} className="px-8 h-10 font-mono text-sm" style={{ background: '#ef444415', border: '1px solid #ef444450', color: '#ef4444' }}>
                        {intelligenceLoading ? <><Loader2 size={14} className="mr-2 animate-spin" />Running...</> : <><Shield size={14} className="mr-2" />Run Intelligence Analysis</>}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-gray-400">{iocResult.total_iocs} indicators extracted</span>
                        <button onClick={runIntelligence} disabled={intelligenceLoading} className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300"><RefreshCw size={11} /> Re-run</button>
                      </div>
                      {([['urls', 'URLs', '#ef4444'], ['domains', 'Domains', '#f97316'], ['handles', 'Handles', '#3b82f6'], ['wallets', 'Crypto Wallets', '#eab308'], ['emails', 'Emails', '#a855f7'], ['phones', 'Phone Numbers', '#22c55e']] as const).map(([key, label, color]) => {
                        const items: any[] = iocResult[key] ?? [];
                        if (!items.length) return null;
                        return (
                          <div key={key} className="rounded-lg p-5" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                            <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color }}>{label} <span className="text-gray-600">({items.length})</span></p>
                            <div className="space-y-1.5">
                              {items.slice(0, 20).map((ioc: any) => (
                                <div key={ioc.value} className="flex items-center justify-between py-1.5 px-3 rounded" style={{ background: '#111', border: '1px solid #1e1e1e' }}>
                                  <span className="text-xs font-mono text-gray-300 truncate max-w-[70%]">{ioc.value}</span>
                                  <span className="text-[10px] font-mono flex-shrink-0 ml-2" style={{ color: `${color}90` }}>seen {ioc.count}x</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="alerts" className="flex-1 m-0" style={{ overflowY: 'auto', minHeight: 0 }}>
                <AlertsTab />
              </TabsContent>

              <TabsContent value="export" className="flex-1 m-0" style={{ overflowY: 'auto', minHeight: 0 }}>
                <div className="p-6 max-w-2xl">
                  <p className="text-xs font-mono uppercase tracking-widest text-gray-600 mb-6">Export Intelligence Package</p>
                  {!hasData ? (
                    <div className="rounded-lg p-6 text-center" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}><p className="text-sm text-gray-600">No data to export. Run a search first.</p></div>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-lg p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-3">TLP Classification</p>
                        <div className="flex gap-2">
                          {(['WHITE', 'GREEN', 'AMBER', 'RED'] as const).map(level => {
                            const TLP_C: Record<string, string> = { WHITE: '#e5e7eb', GREEN: '#00B050', AMBER: '#FFC000', RED: '#ef4444' };
                            return (
                              <button key={level} onClick={() => setTlpLevel(level)} className="flex-1 py-1.5 rounded text-[11px] font-mono font-bold"
                                style={{ background: tlpLevel === level ? `${TLP_C[level]}25` : 'transparent', border: `1px solid ${tlpLevel === level ? TLP_C[level] : '#2a2a2a'}`, color: tlpLevel === level ? TLP_C[level] : '#4b5563' }}>
                                TLP:{level}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="rounded-lg p-5 flex items-start justify-between" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                        <div><div className="flex items-center gap-2 mb-1"><FileText size={14} style={{ color: '#00aaff' }} /><span className="text-sm font-mono text-white">PDF Intelligence Report</span></div></div>
                        <Button onClick={handleExportPDF} disabled={pdfLoading} className="h-9 px-4 text-xs font-mono flex-shrink-0" style={{ background: '#00aaff15', border: '1px solid #00aaff40', color: '#00aaff' }}>
                          {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : <><Download size={13} className="mr-1.5" />Download PDF</>}
                        </Button>
                      </div>
                      <div className="rounded-lg p-5 flex items-start justify-between" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                        <div><div className="flex items-center gap-2 mb-1"><Shield size={14} style={{ color: '#f97316' }} /><span className="text-sm font-mono text-white">STIX 2.1 Bundle</span></div></div>
                        <Button onClick={handleExportStix} disabled={stixLoading} className="h-9 px-4 text-xs font-mono flex-shrink-0" style={{ background: '#f9731615', border: '1px solid #f9731640', color: '#f97316' }}>
                          {stixLoading ? <Loader2 size={13} className="animate-spin" /> : <><Download size={13} className="mr-1.5" />Export STIX</>}
                        </Button>
                      </div>
                      <div className="rounded-lg p-5 flex items-start justify-between" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
                        <div><div className="flex items-center gap-2 mb-1"><Shield size={14} style={{ color: '#a855f7' }} /><span className="text-sm font-mono text-white">JSON Data Export</span></div></div>
                        <Button onClick={handleExportJSON} className="h-9 px-4 text-xs font-mono flex-shrink-0" style={{ background: '#a855f715', border: '1px solid #a855f740', color: '#a855f7' }}>
                          <Download size={13} className="mr-1.5" />Download JSON
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </>}
      </div>

      {/* Metric Modal */}
      {metricModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => setMetricModal(null)}>
          <div className="rounded-xl overflow-hidden w-full max-w-sm" style={{ background: '#0f0f0f', border: `1px solid ${metricModal.color}30` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${metricModal.color}20` }}>
              <span className="text-sm font-mono font-bold" style={{ color: metricModal.color }}>{metricModal.label}</span>
              <button onClick={() => setMetricModal(null)} className="text-gray-600 hover:text-white"><XIcon size={14} /></button>
            </div>
            <div className="p-5 space-y-3">
              {metricModal.rows.map((row, i) => (
                <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #1a1a1a' }}>
                  <span className="text-xs text-gray-500">{row.label}</span><span className="text-xs font-mono font-bold text-white">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {postModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.90)' }} onClick={() => setPostModal(null)}>
          <div className="rounded-xl overflow-hidden w-full max-w-2xl max-h-[90vh] flex flex-col" style={{ background: '#0f0f0f', border: `1px solid ${PC[postModal.platform] ?? PC.default}30` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid #1a1a1a' }}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-bold text-white">{postModal.author}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: `${PC[postModal.platform] ?? PC.default}15`, color: PC[postModal.platform] ?? PC.default }}>{PL[postModal.platform] ?? postModal.platform}</span>
              </div>
              <button onClick={() => setPostModal(null)} className="text-gray-600 hover:text-white"><XIcon size={14} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              <div className="rounded-lg p-4" style={{ background: '#151515', border: '1px solid #222' }}>
                <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{postModal.text}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-4" style={{ background: '#151515', border: '1px solid #222' }}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-3">Engagement</p>
                  {[['Likes', postModal.likes], ['Shares', postModal.shares], ['Comments', postModal.comments], ['Views', postModal.views]].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-xs py-1" style={{ borderBottom: '1px solid #1a1a1a' }}><span className="text-gray-600">{l}</span><span className="font-mono text-white">{fmtNum(v as number)}</span></div>
                  ))}
                </div>
                <div className="rounded-lg p-4" style={{ background: '#151515', border: '1px solid #222' }}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mb-3">Author</p>
                  {[['Account', `@${postModal.author_id || postModal.author}`], ['Followers', fmtNum(postModal.author_followers)], ['Verified', postModal.author_verified ? 'Yes' : 'No']].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-xs py-1" style={{ borderBottom: '1px solid #1a1a1a' }}><span className="text-gray-600">{l}</span><span className="font-mono text-white">{v}</span></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 flex-shrink-0" style={{ borderTop: '1px solid #1a1a1a' }}>
              {postModal.url && <a href={postModal.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}><Link2 size={11} /> Open Original</a>}
              <span className="text-[10px] text-gray-600 ml-auto">{formatDistanceToNow(new Date(postModal.timestamp), { addSuffix: true })}</span>
            </div>
          </div>
        </div>
      )}

      {/* Stream Detail Modal */}
      {streamModal && (
        <StreamDetailModal
          stream={streamModal}
          onClose={() => setStreamModal(null)}
          onDelete={id => deleteStreamMutation.mutate(id)}
          onUpdated={() => queryClient.invalidateQueries({ queryKey: ['social-streams'] })}
        />
      )}
    </div>
  );
};

export default SocialIntelligence;
